import { queryOptions, useQuery } from '@tanstack/react-query';

import { TourKeys } from '@cast/constants';
import { WorkloadFiltersResponse } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';
import { useTourStepId } from 'core/tour-v2/hooks/useTourStepId';

const WOOP_WORKLOAD_FILTERS_QUERY_KEY = 'woop-workload-filters';

type UseWorkloadFiltersQueryArgs = {
  clusterId: string;
  optimizedOnly?: boolean;
  enabled?: boolean;
};

const getWorkloadFiltersQueryKey = (params: {
  clusterId: string;
  optimizedOnly?: boolean;
  tourStepId?: string | null;
}) =>
  [
    WOOP_WORKLOAD_FILTERS_QUERY_KEY,
    params.clusterId,
    params.optimizedOnly,
    params.tourStepId,
  ] as const;

const getWorkloadFiltersQueryOptions = ({
  clusterId,
  optimizedOnly,
  tourStepId,
  enabled,
}: UseWorkloadFiltersQueryArgs & { tourStepId?: string | null }) =>
  queryOptions<WorkloadFiltersResponse>({
    queryKey: getWorkloadFiltersQueryKey({
      clusterId: clusterId,
      optimizedOnly,
      tourStepId,
    }),
    queryFn: async () => {
      const { data } = await apiClient.workloadOptimization.getWorkloadFilters({
        clusterId,
        managementOptions: optimizedOnly ? ['MANAGED'] : undefined,
      });
      return data;
    },
    enabled,
  });

export const useWorkloadFiltersQuery = ({
  clusterId,
  enabled,
  optimizedOnly,
}: UseWorkloadFiltersQueryArgs) => {
  const tourStepId = useTourStepId(TourKeys.OpinionatedPlatform);

  const { isLoading, data, error, refetch } = useQuery(
    getWorkloadFiltersQueryOptions({
      clusterId,
      enabled,
      optimizedOnly,
      tourStepId,
    })
  );

  return { isLoading, data, error, refetch };
};
